import React, {useEffect, useMemo} from 'react';
import {Box, Grid} from '@mui/material';
import {get} from 'lodash';
import {
  ActionType,
  AutoField,
  SelectGroupOption,
  SelectOption,
} from '../../../components/AutoForm/AutoFieldNew';
import {PlanProviderSelectOption} from '../../../hooks/usePlanNamesProviders';
import {useAccountContext} from '../../../contexts/AccountContext';
import {OVRecommendActionEnum} from '../../../schema-types/globalTypes';
import {builderFieldsType} from '../CreateBuilder/builderFields';
import type {newPlanFields} from './builderSetUpNewFields';
import {
  setUpNewPlanProviderField as builderPlanProviderField,
  setUpNewPlanTypeField as builderPlanTypeField,
  inTrustField,
  investorCategoryField,
} from './builderSetUpNewFields';
import type {modelPortfolioField} from '../BuilderModify/builderModifyFieldTypes';
import {withdrawalAmountField as builderAmountField} from '../BuilderWithdrawal/builderWithdrawalFields';
import {BuilderMandatesWrapper as MandatesWrapper} from '../pages/BuilderMandatesModal';
import {hasWrapper} from '../CreateBuilder/BuilderSourceDestination/builderUtils';
import {
  getFilteredProductNames,
  getNewPlanProviders,
  updateIsSaltusProvider,
  deriveIsNewLisa,
  setDefaultSaltusSippPlanType,
  clearMandateOnProviderBlur,
  useDefaultProduct,
} from '../hooks/builderSetUpNewHooks';
import {
  AutoFieldHelpers,
  useAutoForm,
  useAutoField,
} from '../../../components/AutoForm/useAutoForm';
import {useBuilderSPPProviders} from '../CreateBuilder/useBuilderSPPProviders';
import {useBuilderContext} from '../CreateBuilder/useBuilderContext';
import {BuilderPlanFeesField} from '../CreateBuilder/BuilderPlanFees';
import {includesSaltus} from '../../../utils/includesSaltus';
import {sanitiseProductType} from '../../../utils/sanitiseProductType';
import validateUUID from '../../../utils/validateUUID';
import {ParentGuardianSearch} from '../../../components/ParentGuardianSearch/ParentGuardianSearch';
import {useIsMinor} from '../hooks/createBuilderHooks';
import {isBondPlan} from './bondSetUpNew';

export interface BuilderNewPlanFormProps {
  isLowesPension: boolean;
  isTransactBond: boolean;
  productProvidersOptionGroups: SelectGroupOption<string>[];
  productProviders: PlanProviderSelectOption[] | undefined;
  productNames: SelectOption<string>[] | undefined;
}

export {
  updateIsSaltusProvider,
  deriveIsNewLisa,
  setDefaultSaltusSippPlanType,
  clearMandateOnProviderBlur,
};

export const useUpdateIsSaltusProvider = ({
  isSaltusProviderHelpers,
  productProviders,
  planProvider,
}: {
  isSaltusProviderHelpers: AutoFieldHelpers<boolean>;
  productProviders: PlanProviderSelectOption[] | undefined;
  planProvider: string | undefined;
}) => {
  useEffect(() => {
    updateIsSaltusProvider({isSaltusProviderHelpers, productProviders, planProvider});
  }, [planProvider, productProviders]);
};

export const useSetDefaultSaltusSippPlanType = ({
  displayProductNames,
  isSaltusSIPP,
}: {
  displayProductNames: SelectOption<string>[] | undefined;
  isSaltusSIPP: boolean;
}) => {
  const [, , productNameIdHelpers] = useAutoField<string | undefined>(
    'setUpNew.parent.productNameId'
  );
  useEffect(() => {
    setDefaultSaltusSippPlanType({
      displayProductNames,
      isSaltusSIPP,
      productNameIdHelpers,
    });
  }, [isSaltusSIPP, displayProductNames]);
};

export const BuilderNewPlanForm: React.FC<BuilderNewPlanFormProps> = ({
  isLowesPension,
  isTransactBond,
  productProvidersOptionGroups,
  productProviders,
  productNames,
}) => {
  const isMinor = useIsMinor();

  const [mandateField, , mandateHelpers] = useAutoField<modelPortfolioField | undefined>(
    'setUpNew.parent.model'
  );
  const {isSPPDA} = useBuilderContext();
  const {values} = useAutoForm<builderFieldsType>();
  const {mainPlanType} = values;
  const needsInTrust = useMemo(() => mainPlanType === 'gia', [mainPlanType]);
  const [mainPlanTypeField] =
    useAutoField<builderFieldsType['mainPlanType']>('mainPlanType');
  const [newPlanFieldObj] = useAutoField<newPlanFields>('setUpNew');

  const displayProductNames = useMemo(
    () => getFilteredProductNames({productNames, minor: isMinor, mainPlanType}),
    [productNames, isMinor, mainPlanType]
  );
  useDefaultProduct(displayProductNames);

  const {disableProviderField} = useBuilderSPPProviders(
    `setUpNew.parent.planProvider`,
    productProvidersOptionGroups,
    null,
    mainPlanTypeField.value === 'pension',
    false
  );

  // const isNewLisa = useDeriveIsNewLisa(formikKeyPrefix);

  const isSaltusSIPP = useMemo(() => {
    const plan = productProviders?.find(
      (x) => x.value === newPlanFieldObj.value?.parent?.planProvider
    );
    if (plan) {
      return plan.label.toLocaleLowerCase() === 'saltus sipp';
    }
    return false;
  }, [newPlanFieldObj.value?.parent?.planProvider, productProviders]);

  const isSaltus = useMemo(() => {
    const plan = productProviders?.find(
      (x) => x.value === newPlanFieldObj.value?.parent?.planProvider
    );
    if (plan) {
      return plan?.isSaltus;
    }
    return false;
  }, [newPlanFieldObj.value?.parent?.planProvider, productProviders]);

  useSetDefaultSaltusSippPlanType({displayProductNames, isSaltusSIPP});

  const showParentMandate = isLowesPension || isTransactBond;

  return (
    <>
      <Grid pt={2} flex={1} container direction="row" spacing={2}>
        <Grid item xs={6}>
          <AutoField
            field={{
              ...builderPlanProviderField,
              disabled: !productProvidersOptionGroups.length || disableProviderField,
              label: productProvidersOptionGroups.length
                ? builderPlanProviderField.label
                : 'Loading plan providers...',
              type: ActionType.selectAutocomplete,
              id: `setUpNew.parent.planProvider`,
              options: getNewPlanProviders(productProviders),
              groupBy: (option) => option.group || '',
            }}
            onBlur={() => {
              clearMandateOnProviderBlur({mandateHelpers});
            }}
          />
        </Grid>
        {isSaltusSIPP ? null : (
          <Grid item xs={6}>
            <AutoField
              field={{
                ...builderPlanTypeField,
                disabled: !displayProductNames?.length,
                label: builderPlanTypeField.label,
                id: `setUpNew.parent.productNameId`,
                options: displayProductNames || [],
              }}
            />
          </Grid>
        )}
        {/* If a transfer to a new plan. Show added amount */}
        {values.planAction === OVRecommendActionEnum.TRANSFER ? (
          <Grid item xs={6}>
            <AutoField
              field={{
                ...builderAmountField,
                disabled: true,
                id: `transfer.combinedOneOffMovingAmount`,
                label: `Personal Single Amount${
                  values.mainPlanType === 'pension' ? ' (Gross)' : ''
                }`,
              }}
            />
          </Grid>
        ) : null}
        {showParentMandate && (
          <Grid item xs={6}>
            <MandatesWrapper
              showCurrentMandate={false}
              planProviderOptions={productProviders}
              formikKeyPrefix="setUpNew"
            />
          </Grid>
        )}
        <Grid item xs={6}>
          <AutoField field={investorCategoryField} />
        </Grid>
        {needsInTrust && !isSPPDA && (
          <Grid item xs={6}>
            <AutoField field={{...inTrustField, id: `setUpNew.parent.inTrust`}} />
          </Grid>
        )}
        {isMinor && (
          <Grid item xs={6}>
            <ParentGuardianSearch formikKeyPrefix="setUpNew.parent" />
          </Grid>
        )}
        <Grid item xs={12}>
          <BuilderPlanFeesField
            inModify={false}
            hasSubplan={(values.setUpNew?.subplans?.length ?? 0) > 0}
            requiresMandate={showParentMandate}
            productId={get(values, `setUpNew.parent.productNameId`, null)}
            mandateId={
              mandateField && typeof mandateField.value === 'object'
                ? mandateField.value?.id
                : undefined
            }
            providerId={get(values, `setUpNew.parent.planProvider`, null)}
            formikKeyPrefixWithoutFee="setUpNew.parent"
            pickFpFee={isBondPlan(mainPlanType)}
            // disabled={isNewLisa} // No Ongoing Fee on New LISA
          />
        </Grid>
      </Grid>
      <Box mb={2} />
    </>
  );
};
